import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { extractOAuthParams } from '../../utils/oauth';
import { popReturnUrl } from '@low6-pickem/frontend-shared';

/**
 * OAuth Callback Component
 * Handles the OAuth callback route and processes authentication
 */
export function OAuthCallback() {
  const navigate = useNavigate();
  const { loginWithOAuthCode } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract OAuth parameters from URL
        const params = extractOAuthParams();

        // Check for OAuth errors
        if (params.error) {
          setError(params.error_description || params.error);
          setIsProcessing(false);
          return;
        }

        // Validate token (backend already processed OAuth and returned token)
        if (!params.token) {
          setError('Missing authentication token');
          setIsProcessing(false);
          return;
        }

        // Extract user data from URL params
        const user = {
          sso_user_id: params.sso_user_id || '',
          username: params.username || '',
        };

        // Update auth context with token and user data
        await loginWithOAuthCode(params.token, user);

        // Redirect to original deep-link URL (if any) or home
        navigate(popReturnUrl() ?? '/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [navigate, loginWithOAuthCode]);

  if (isProcessing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#53d337] mx-auto mb-4"></div>
          <p className="text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            Authenticating...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2
            className="text-white text-xl font-bold mb-2"
            style={{ fontFamily: '"Saira Extra Condensed", sans-serif' }}
          >
            Authentication Failed
          </h2>
          <p className="text-gray-400 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-[#53d337] text-black rounded-lg"
            style={{
              fontFamily: '"Saira Extra Condensed", sans-serif',
              fontWeight: 800,
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null;
}
